import { trim } from 'lodash';
import _axios from '../api';
import syncApi, { SyncApi } from '../syncApi';
import { queryParams } from '../utils';
import { API_GATEWAY_INTEGRATION_BUSINESS_CENTRAL } from '../utils/constants';
import { getOrCreateGatewayStore } from './gatewayActions';
import { isProduction } from '../utils/environment';
import { saveErrorMessage } from '../utils/apiErrors';

const API = '/stores';

// Only the newest list request may update the store: dataCentreActions.select()'s unfiltered fetch
// and the Stores view's filtered one can both be in flight, and must not land out of order.
let latestGetAll = 0;

export function getAllByDataCentre(
  dataCentre = {},
  page = 0,
  size = 20,
  sort = 'id',
  sortDir = 'DESC',
  searchRequest = null
) {
  return async (dispatch, getState) => {
    // a save/delete refresh for a data centre the operator has since switched away from
    if (dataCentre.id !== getState().dataCentre.selected.id) return;
    const requestId = ++latestGetAll;
    dispatch({ type: 'STORE_LOADING' });
    try {
      const response = await _axios.get(
        `${API}/data_center/${dataCentre.id}?page=${page}&size=${size}&sort=${sort},${sortDir}${queryParams(
          searchRequest
        )}`
      );
      if (requestId !== latestGetAll) return;
      dispatch({
        type: 'STORE_GETALL_SUCCESS',
        payload: response.data,
      });
    } catch (err) {
      if (requestId !== latestGetAll) return;
      dispatch({
        type: 'STORE_GETALL_ERROR',
        payload: err.message,
      });
    }
  };
}

export function getStoreTypeCount(dataCentre = {}) {
  return function (dispatch) {
    dispatch({ type: 'STORE_LOADING' });
    _axios
      .get(`${API}/store_type_count/${dataCentre.id}`)
      .then((response) => {
        dispatch({
          type: 'STORE_GETSTORETYPECOUNT_SUCCESS',
          payload: response.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: 'STORE_GETSTORETYPECOUNT_ERROR',
          payload: err.message,
        });
      });
  };
}

export function getAllByEnvironment(
  environment = {},
  fuzzyString = '',
  dataCentre = {},
  page = 0,
  size = 20,
  sort = 'id',
  sortDir = 'DESC'
) {
  return async function (dispatch) {
    try {
      let response;
      if (environment.name.toLowerCase() == process.env.NODE_ENV) {
        response = await _axios.get(
          `${API}/data_center/${dataCentre.id}?page=${page}&size=${size}&sort=${sort},${sortDir}${queryParams({
            fuzzyString,
          })}`
        );
      } else {
        response = await _axios.get(
          `${API}/environments/${environment.name}?fuzzyString=${trim(
            fuzzyString
          )}&page=${page}&size=${size}&sort=${sort},${sortDir}`
        );
      }
      dispatch({
        type: 'STORE_GETALL_BY_ENVIRONMENT_SUCCESS',
        payload: response.data,
      });
    } catch (err) {
      console.error(err);
      dispatch({
        type: 'STORE_GETALL_BY_ENVIRONMENT_ERROR',
        payload: err.message,
      });
    }
  };
}

export function getOne(storeId) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      _axios
        .get(API + '/' + storeId)
        .then((response) => {
          dispatch({
            type: 'STORE_GETONE_SUCCESS',
            payload: response.data,
          });
          sessionStorage.setItem('storePrimaryDomain', response.data.primaryDomain);
          const dataCentreEndpoint = response.data.dataCentre && response.data.dataCentre.endpoint;
          if (dataCentreEndpoint) {
            sessionStorage.setItem('storeDataCentreEndpoint', dataCentreEndpoint);
          } else {
            sessionStorage.removeItem('storeDataCentreEndpoint');
          }
          resolve(response.data);
        })
        .catch((err) => {
          dispatch({
            type: 'STORE_GETONE_ERROR',
            payload: err.message,
          });
          sessionStorage.removeItem('storePrimaryDomain');
          sessionStorage.removeItem('storeDataCentreEndpoint');
          reject(err);
        });
    });
}

/**
 * create (POST) || update (PUT)
 * @param {*} store
 */
export function save(store) {
  return async (dispatch) => {
    if (store.id) {
      try {
        await _axios.put(API, store);
        await dispatch({ type: 'STORE_UPDATE_SUCCESS' });
        await dispatch(getAllByDataCentre(store.dataCentre));
        await dispatch(clear());
      } catch (err) {
        dispatch({
          type: 'STORE_UPDATE_ERROR',
          payload: saveErrorMessage(err),
        });
        // rethrow: the caller keeps its form open on a rejection, which is where the error renders
        throw err;
      }
    } else {
      // Everything up to and including the POST is the SAVE. A failure here
      // created nothing, so it reports a save error and rejects, which is what
      // keeps the form open on the offending input.
      let store_id;
      try {
        const resErp = await _axios.get(`erp_types/${store.erpType.id}`);
        if (resErp && resErp.data && resErp.data.code === 'MS_Dynamics_BC') {
          store.orderIntegrationVersion = 'VERSION_2';
        }
        const resStore = await _axios.post(API, store);
        store_id = resStore.data;
      } catch (err) {
        dispatch({
          type: 'STORE_CREATE_ERROR',
          payload: saveErrorMessage(err),
        });
        // rethrow: the caller keeps its form open on a rejection, which is where the error renders
        throw err;
      }

      // The store now EXISTS. Nothing below may report a save failure or reject:
      // the form would stay open over a created store, and because the form never
      // receives the new id, the obvious retry re-enters this branch and POSTs a
      // SECOND store — a duplicate, or a 409 for the domain it just took.
      await dispatch({ type: 'STORE_CREATE_SUCCESS' });
      try {
        await dispatch(getAllByDataCentre(store.dataCentre));
        await dispatch(async () => {
          const resStore = await _axios.get(`stores/${store_id}`);
          if (resStore && resStore.data && resStore.data.erpType.code === 'MS_Dynamics_BC') {
            const realStore = resStore.data;

            // gateway
            await dispatch(getOrCreateGatewayStore(realStore));
          }
        });
      } catch (err) {
        // Reported against the created store, never as "your save failed".
        dispatch({
          type: 'STORE_CREATE_PROVISION_ERROR',
          payload: { storeId: store_id, message: saveErrorMessage(err) },
        });
      }

      await dispatch(clear());
    }
  };
}

export function updateStoreStatus(store, storeStatus) {
  return async (dispatch) => {
    try {
      await _axios.put(API + '/update_status/' + store.id, storeStatus);
      dispatch({ type: 'STORE_UPDATESTATUS_SUCCESS' });
      dispatch(getAllByDataCentre(store.dataCentre));
    } catch (error) {
      dispatch({
        type: 'STORE_UPDATESTATUS_ERROR',
        payload: error.message,
      });
    }
  };
}

// Clears a previous save failure without touching `one` — the form dispatches this as it
// opens so a stale error from an earlier attempt is not shown against fresh input.
export function clearError() {
  return function (dispatch) {
    dispatch({ type: 'STORE_ERROR_CLEAR' });
  };
}

export function clear() {
  return function (dispatch) {
    Promise.all([dispatch({ type: 'STORE_CLEAR' })]);
  };
}

export function remove(store) {
  return async (dispatch) => {
    try {
      await _axios.delete(API + '/' + store.id);
      dispatch({ type: 'STORE_DELETE_SUCCESS' });
      dispatch(getAllByDataCentre(store.dataCentre));
      sessionStorage.removeItem('storePrimaryDomain');
      sessionStorage.removeItem('storeDataCentreEndpoint');
    } catch (error) {
      dispatch({
        type: 'STORE_DELETE_ERROR',
        payload: error.message,
      });
    }
  };
}
